import { motion, useInView, type Variants } from 'framer-motion';
import React, { useRef } from 'react';

const animations = {
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideUp: {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 },
  },
  scaleIn: {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
  },
} satisfies Record<string, Variants>;

interface AnimateOnScrollProps {
  children: React.ReactNode;
  className?: string;
  animation?: keyof typeof animations;
  duration?: number;
  delay?: number;
  once?: boolean;
  amount?: number;
  /** Overrides `animation` */
  customVariants?: Variants;
}

export const AnimateOnScroll: React.FC<AnimateOnScrollProps> = ({
  children,
  className = '',
  animation = 'fadeIn',
  duration = 0.5,
  delay = 0,
  once = true,
  amount = 0.3,
  customVariants,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount });

  const variants = customVariants || animations[animation];

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants}
      transition={{
        duration,
        delay,
        ease: [0.4, 0, 0.2, 1], // smooth easing
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};
